import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link as LinkIcon, Mail, Lock, Chrome, ChevronDown, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, usersAPI } from '../lib/api';
import { HBCU_LIST } from '../lib/hbcuList';

// Curated Unsplash images — professionals, graduates, collaboration, history
const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&q=80', // Woman and man at monitor
  'https://images.unsplash.com/photo-1521790361543-f645cf042ec4?w=1600&q=80', // Hands on macbook flat lay
  'https://images.unsplash.com/photo-1573167627769-e201a7ddf409?w=1600&q=80', // Women at conference table
  'https://images.unsplash.com/photo-1679117349740-c46c819d0373?w=1600&q=80', // Man in suit on phone
  'https://images.unsplash.com/photo-1622842547373-5aa4bf062dc5?w=1600&q=80', // Person in black long sleeve
  'https://images.unsplash.com/photo-1674052353268-6412e0781b4a?w=1600&q=80', // Men sitting on stool
  'https://images.unsplash.com/photo-1709811240710-cff5f04deb44?w=1600&q=80', // Woman in graduation cap
  'https://images.unsplash.com/photo-1764264829056-2775046bcf42?w=1600&q=80', // Woman in suit and sunglasses on rooftop
  'https://images.unsplash.com/photo-1765648684898-2213d6b11c5d?w=1600&q=80', // Woman at outdoor cafe with laptop
  'https://images.unsplash.com/photo-1765648496267-a0b528fb97fb?w=1600&q=80', // Woman typing on laptop at desk
  'https://images.unsplash.com/photo-1616804947838-6646ae0e423d?w=1600&q=80', // Man in blue suit beside black car
  'https://images.unsplash.com/photo-1739289696461-76363e419fae?w=1600&q=80', // Man sitting on chair reading
  'https://images.unsplash.com/photo-1552652893-2aa10a0ab4df?w=1600&q=80', // Man standing near high-rise building
  'https://images.unsplash.com/photo-1615572359976-1fe39507ed7b?w=1600&q=80', // Man in gray shirt near bridge
  'https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?w=1600&q=80', // Man in yellow blazer smiling
  'https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=1600&q=80', // Woman in white collared shirt city night
  'https://images.unsplash.com/photo-1597711662967-b5dac822f5ac?w=1600&q=80', // March on Washington demonstrators
  'https://images.unsplash.com/photo-1597702693388-c985cfd3eaea?w=1600&q=80', // Reflecting pool March on Washington 1963
  'https://images.unsplash.com/photo-1648009847543-7491343aaec8?w=1600&q=80', // I am Black History t-shirt
  'https://images.unsplash.com/photo-1697183381312-0f2ac034bddc?w=1600&q=80', // Men with signs at US Capitol civil rights
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [institution, setInstitution] = useState('');
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState('');
  const [bgLoaded, setBgLoaded] = useState(false);
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/feed';

  // Pick a random background image on mount
  useEffect(() => {
    const img = BACKGROUND_IMAGES[Math.floor(Math.random() * BACKGROUND_IMAGES.length)];
    setBgImage(img);
    const preload = new Image();
    preload.onload = () => setBgLoaded(true);
    preload.src = img;
  }, []);

  const filteredInstitutions = HBCU_LIST.filter(inst =>
    inst.name.toLowerCase().includes(institutionSearch.toLowerCase()) ||
    inst.state.toLowerCase().includes(institutionSearch.toLowerCase())
  );

  const handleSelectInstitution = (inst) => {
    setInstitution(inst.name);
    setShowInstitutionDropdown(false);
    setInstitutionSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!institution) {
      toast.error('Please select your institution first');
      return;
    }
    setLoading(true);
    try {
      const userData = await login(email, password);
      // Update institution if user selected one and it's not already set
      if (institution && (!userData?.institution || userData.institution !== institution)) {
        try { await usersAPI.updateProfile({ institution }); } catch {}
      }
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      const msg = error?.code === 'auth/invalid-credential' ? 'Invalid email or password'
        : error?.code === 'auth/user-not-found' ? 'No account found with this email'
        : error?.code === 'auth/wrong-password' ? 'Incorrect password'
        : error?.message || formatApiError(error);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!institution) {
      toast.error('Please select your institution first');
      return;
    }
    try {
      const result = await loginWithGoogle();
      if (result) {
        if (institution && (!result?.institution || result.institution !== institution)) {
          try { await usersAPI.updateProfile({ institution }); } catch {}
        }
        toast.success('Welcome!');
        navigate(from, { replace: true });
      }
    } catch (e) {
      if (e?.code === 'auth/popup-closed-by-user') return; // User cancelled, no error
      toast.error(e?.message || 'Sign-in failed');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Background Image */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-slate via-dark-slate/85 to-dark-slate/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-slate via-dark-slate/40 to-dark-slate/20" />

        {/* Branding overlay */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20">
              <LinkIcon className="w-8 h-8 text-white" />
            </div>
            <span className="font-heading font-extrabold text-3xl text-white [text-shadow:_0_2px_8px_rgba(0,0,0,0.5)]">GradLink</span>
          </div>

          <div className="max-w-lg bg-dark-slate/60 backdrop-blur-md rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h2 className="font-heading font-extrabold text-5xl text-white leading-tight mb-4 [text-shadow:_0_2px_12px_rgba(0,0,0,0.7)]">
              Your Alumni Network, Reimagined
            </h2>
            <p className="text-white/90 text-lg leading-relaxed [text-shadow:_0_1px_6px_rgba(0,0,0,0.6)]">
              Connect with fellow HBCU alumni for mentorship, career opportunities,
              services, and meaningful relationships.
            </p>
            <div className="flex items-center space-x-6 mt-8">
              {[
                { num: 'Thousands', label: 'of Alumni' },
                { num: 'Thousands', label: 'of Providers' },
                { num: '107', label: 'HBCUs' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="font-heading font-bold text-2xl text-white [text-shadow:_0_2px_8px_rgba(0,0,0,0.6)]">{stat.num}</p>
                  <p className="text-white/80 text-sm [text-shadow:_0_1px_4px_rgba(0,0,0,0.5)]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-white/50 text-xs">
            Photos from Unsplash
          </p>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 lg:max-w-xl flex items-center justify-center p-6 bg-white relative">
        {/* Mobile background (shown on small screens) */}
        <div className="absolute inset-0 lg:hidden overflow-hidden">
          <div
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${bgLoaded ? 'opacity-40' : 'opacity-0'}`}
            style={{ backgroundImage: `url(${bgImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-white/90" />
        </div>

        <div className="relative w-full max-w-md z-10">
          {/* Mobile Logo */}
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-dark-slate rounded-2xl flex items-center justify-center shadow-lg">
                <LinkIcon className="w-7 h-7 text-mint-fresh" />
              </div>
              <h1 className="font-heading font-extrabold text-3xl text-dark-slate">GradLink</h1>
            </div>
            <p className="text-slate-gray text-sm">Your Alumni Network, Reimagined</p>
          </div>

          {/* Desktop heading */}
          <div className="hidden lg:block mb-8">
            <h2 className="font-heading font-bold text-2xl text-dark-slate mb-1">Welcome Back</h2>
            <p className="text-slate-gray">Sign in to your alumni network</p>
          </div>

          {/* Institution Selection — REQUIRED */}
          <div className="mb-5">
            <Label className="text-dark-slate font-semibold text-sm mb-2 block">
              Select Your Institution <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <button type="button" onClick={() => setShowInstitutionDropdown(!showInstitutionDropdown)}
                className={`w-full h-12 px-4 rounded-xl border flex items-center justify-between text-left transition-colors ${
                  institution ? 'bg-mint-fresh/10 border-emerald-300 text-dark-slate' : 'bg-slate-50 border-slate-200 text-slate-gray hover:border-slate-300'
                }`}>
                <span className={institution ? 'font-medium' : ''}>{institution || 'Choose your HBCU...'}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${showInstitutionDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showInstitutionDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowInstitutionDropdown(false)} />
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden">
                    <div className="p-3 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                        <input type="text" placeholder="Search HBCUs..." value={institutionSearch}
                          onChange={(e) => setInstitutionSearch(e.target.value)}
                          className="w-full h-10 pl-9 pr-4 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-mint-fresh" autoFocus />
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto overscroll-contain">
                      {filteredInstitutions.length === 0 ? (
                        <div className="py-6 text-center text-slate-gray text-sm">No institutions found</div>
                      ) : (
                        filteredInstitutions.map((inst) => (
                          <button key={inst.id} onClick={() => handleSelectInstitution(inst)}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-mint-fresh/20 flex items-center justify-between transition-colors">
                            <div>
                              <span className="text-dark-slate font-medium">{inst.name}</span>
                              <span className="text-slate-gray ml-2 text-xs">({inst.state})</span>
                            </div>
                            {institution === inst.name && <Check className="w-4 h-4 text-emerald-600" />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {institution && (
              <p className="text-emerald-600 text-xs font-medium mt-1.5">
                GradLink for {institution}
              </p>
            )}
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-dark-slate font-medium text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                <Input id="email" type="email" placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required data-testid="login-email" />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-dark-slate font-medium text-sm">Password</Label>
                <button type="button"
                  onClick={async () => {
                    if (!email) { toast.error('Enter your email first'); return; }
                    try { await resetPassword(email); toast.success('Password reset email sent!'); }
                    catch { toast.error('Failed to send reset email'); }
                  }}
                  className="text-xs text-slate-gray hover:text-dark-slate transition-colors">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                <Input id="password" type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required data-testid="login-password" />
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold hover:bg-slate-800"
              data-testid="login-submit">
              {loading ? 'Signing in...' : 'Enter GradLink'}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-gray">or</span></div>
          </div>

          <Button type="button" variant="outline" onClick={handleGoogleSignIn}
            className="w-full h-12 rounded-full border-2 border-slate-200 hover:border-dark-slate"
            data-testid="google-login">
            <Chrome className="w-5 h-5 mr-2" />
            Sign in with Google
          </Button>

          <p className="text-center text-slate-gray mt-6 text-sm">
            New to GradLink?{' '}
            <Link to="/register" className="text-dark-slate font-semibold hover:underline">Create Account</Link>
          </p>
          <p className="text-center text-slate-gray mt-3 text-sm">
            Looking to recruit, advertise, or manage your alumni association?{' '}
            <Link to="/role-signup" className="text-dark-slate font-semibold hover:underline">Sign up as a partner &rarr;</Link>
          </p>
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="text-center text-sm font-semibold text-dark-slate mb-1">Own a business?</p>
            <p className="text-center text-xs text-slate-gray mb-3">Get listed in front of 1,000+ HBCU alumni — free during the founding window.</p>
            <Link to="/founding-provider"
              className="flex items-center justify-center gap-2 w-full h-11 rounded-xl border-2 border-dark-slate text-dark-slate font-semibold text-sm hover:bg-dark-slate hover:text-white transition-colors">
              Apply as a Founding Provider &#8599;
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Login;
